import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_ITEMS, FLEX_CONTAINER_COLUMN, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, FONT_WEIGHT, H_100, OVERFLOW, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, createHtmlElement, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogService } from "@cervval.js/common/src/services/log.service";
import { filterNotDefined } from "@cervval.js/common/src/tools/collection.array.tools";
import { ValueOrFunction, anyReallyAny, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { ButtonOptions, ButtonSize, ButtonStyle, CjsButton } from "../../components/button/button";
import { getIconFaName } from "../../components/icon/iconFa.tools";
import { IconFA6 } from "../../components/icon/tools/fontAwesome6IconList.tools";
import { CjsSmartSelect, SmartSelectOptions, ValueType } from "../../components/smartSelect/smartSelect";
import { SELECTION_TYPE } from "../../components/smartTable/selectionModel/smartTableSelectionModel.types";
import { CjsSmartTable, SmartTableColumn, SmartTableOptions } from "../../components/smartTable/smartTable";
import { CjsEditor, EditorOptions, defaultEditorOptions } from "../editor";
import { I18nType, i18n } from "./i18n/i18n";

/** Options of referenceListEditor */
export type ReferenceListEditorOptions<ItemType> = EditorOptions & {
    /** Enable tooltip on remove buttons */
    enableRemoveButtonTooltip?: boolean,

    /** All items will be selected when ReferenceListEditor is init (on first setData) */
    selectAllOnInit?: boolean,

    /** If enable, select will stay open after user selection */
    keepSelectOpenAfterSelect?: boolean,

    /** Override formatter of selectedValue inside selectedValuesTable */
    selectedValueDivFormatter?: (item: ItemType, itemFormatted: AcceptedHtmlElementContent) => AcceptedHtmlElementContent
};

/** Default options of referenceListEditor */
export const defaultReferenceListEditorOptions: ReferenceListEditorOptions<anyReallyAny> = {
    ...defaultEditorOptions
};

/** Default options for remove button of reference list editor */
export const defaultReferenceListEditorRemoveButtonOptions: ButtonOptions = {
    cssClasses: ["selected-value-button"],
    iconMode: "only",
    cssStyle: ButtonStyle.DANGER,
    size: ButtonSize.SMALL,
    icon: {
        classes: ["rotate45"],
        name: getIconFaName(IconFA6.PLUS)
    }
};

// CSS classes constant
export const SELECTED_VALUE_CONTAINER = "selected-value-container";
export const SELECTED_VALUE_DIV = "selected-value-div";


/** Event dispatch on add item is triggered */
export type ReferenceListEditorOnAddEvent<ItemType, Value> = {

    /** Added value  */
    value: Value,

    /** Added item */
    item?: ItemType
}

/** Event dispatch on remove item is triggered */
export type ReferenceListEditorOnRemoveEvent<ItemType, Value> = ReferenceListEditorOnAddEvent<ItemType, Value>;

@CustomElement({
    name: "cjs-reference-list-editor",
    style: require("./referenceListEditor.scss"),
    template: require("./referenceListEditor.html"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN, OVERFLOW.AUTO],
    i18n
})
export class CjsReferenceListEditor<ItemType, Value> extends CjsEditor<Value[], I18nType> {

    @TypedHtmlElements({ ensureLoadedClasses: [CjsSmartSelect] })
    public readonly components!: {
        selectedValuesContainer: HTMLDivElement,
        smartSelect: CjsSmartSelect<ItemType, Value>,
        selectedValuesTable: CjsSmartTable<ItemType>
    };

    @TypedWebComponentOptions(defaultReferenceListEditorOptions)
    public readonly options!: WebComponentOptions<ReferenceListEditorOptions<ItemType>>;

    @Event(NoBubblesEventInit)
    public addEvent!: EventHandler<ReferenceListEditorOnAddEvent<ItemType, Value>>;

    @Event(NoBubblesEventInit)
    public removeEvent!: EventHandler<ReferenceListEditorOnRemoveEvent<ItemType, Value>>;

    @TypedWebComponentOptions(defaultReferenceListEditorRemoveButtonOptions)
    public readonly removeButtonOptions!: WebComponentOptions<ButtonOptions>;

    /** Get smartSelect options */
    public get smartSelectOptions(): WebComponentOptions<SmartSelectOptions<ItemType, Value>> {
        return this.smartSelect.options;
    }

    /** Get smartSelect */
    public get smartSelect(): CjsSmartSelect<ItemType, Value> {
        return this.components.smartSelect;
    }

    /** Get selectedValuesTable options */
    public get selectedValuesTableOptions(): WebComponentOptions<SmartTableOptions<ItemType>> {
        return this.components.selectedValuesTable.options;
    }

    /** 
     * Data set for referenceListEditor
     * To set: use 'setData'
     */
    private _data: ValueOrFunction<ItemType[]> = [];

    /** Get accessor for data of ReferenceListEditor */
    public get data(): ItemType[] {
        // Return data
        return getVOFValue(this._data) ?? [];
    }

    /** Selected values */
    private _selectedValues: ValueType<Value>[] = [];

    constructor(options?: Partial<ReferenceListEditorOptions<ItemType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** When component is initialize */
    public override init(): void {

        super.init();

        this._initSelectedValuesSmartTable();
        this._initListeners();
    }

    /**
     * Function that refresh the displayed referenceListEditor
     */
    public override refresh(): void {
        super.refresh();

        this._refreshDefaultCallbacks();
        this._refreshSmartSelect();
        this._refreshInputValue();
        this._refreshDisabled();
    }

    /**
     * Data used by SmartSelect, the full list of items to display in <select> 
     * @param data Data of smart select
     * @param selectedValues selected values
     * @param refresh boolean to trigger refresh
     */
    public setData(data: ValueOrFunction<ItemType[]>, selectedValues: ValueType<Value>[] = [], refresh = true): void {
        this._data = data;

        // If smartSelect wasn't opened and selectAllOnInit is defined
        if (!this.components.smartSelect.wasSmartListOpened && this.options.selectAllOnInit) {
            this._selectedValues = filterNotDefined(this.data.map(item => this._getItemValue(item)));// Select all values
        } else {
            this._selectedValues = selectedValues;
        }

        if (refresh && this.isInitDone) {
            // if !isInitDone refresh will be called later
            this.refresh();
        }
    }

    /**
     * Set selected values of ref list 
     * @param selectedValues selected values
     * @param refresh boolean to trigger refresh
     * @returns always true as the selected values are always changed
     */
    public setSelectedValues(selectedValues: ValueType<Value>[] = [], refresh = true): boolean {
        this._selectedValues = selectedValues;

        if (refresh) {
            this.refresh();
        }
        return true;
    }

    /**
     * Callback: Retrieve the selected values (often the list of 'ids' selected)
     */
    public getSelectedValues(): ValueType<Value>[] {
        return this._selectedValues;
    }

    /** Return component input */
    public getInput(): CjsSmartSelect<ItemType, Value> {
        return this.components.smartSelect;
    }

    /** Get refList editor value. Return selected values instead of select's value. */
    public override getValue(): Value[] {
        return this._selectedValues;
    }

    /** Refresh smartSelect */
    protected _refreshSmartSelect(): void {
        const dataForSmartSelect = this._getSelectItemsList();
        this.components.smartSelect.options.setWithoutRefresh({
            ...this.smartSelectOptions,
            disabled: this.smartSelectOptions.disabled || !dataForSmartSelect.length,
            data: dataForSmartSelect,
            selectedValue: undefined,// Always remove selected value
            keepSelectOpenAfterSelect: this.options.keepSelectOpenAfterSelect
        });

        // Add value-selected class if at least one value is selected
        toggleClass(this.components.smartSelect.components.smartSelectContainer, this.getSelectedValues().length > 0, "value-selected");

        this.components.smartSelect.refresh();
    }

    /**
     * Refresh list of selectedItems
     */
    protected _refreshInputValue(): void {
        if (this.disabled) { this.components.selectedValuesTable.setData({ data: [] }); }

        toggleVisible(this.components.selectedValuesContainer, this.getSelectedValues().length > 0, Display.FLEX);

        const selectedValues = this.getSelectedValues();
        const selectedItems = filterNotDefined(selectedValues.map(value => this._getItemByValue(this.data, value)));
        this.components.selectedValuesTable.setData({ data: selectedItems });
    }

    /** Init listeners */
    protected _initListeners(): void {

        // listen on smartSelect on select change
        this.components.smartSelect.selectEvent.on(e => {
            if (e.detail.value == null) { return; }
            this._onInputValueChange(e.detail.value);
        });
    }

    /** Init smartTable of selectedValues */
    private _initSelectedValuesSmartTable(): void {

        const table = this.components.selectedValuesTable;

        const columns: SmartTableColumn<ItemType>[] = [
            {
                uid: "content",
                title: "",
                getCellValue: cell => (cell.rowItemData ? this._getItemValue(cell.rowItemData) : undefined),
                renderCell: cell => (cell.rowItemData ? this._createSelectedValueDiv(cell.rowItemData) : "")
            }
        ];

        table.options.setWithoutRefresh({
            columns,
            rowHeight: 36,
            disableHeader: true,
            border: { borderLess: true },
            selection: {
                enableBackgroundDisplay: false,
                isRowSelectable: _ => false,
                selectionType: SELECTION_TYPE.SINGLE
            },
            scrollBar: "standard",
            disableStrippedRows: true,
            parentContainerHasNoHeightConstraint: true
        });

        // Listen to rowDragEnd event, update selectedValues order
        table.dragAndDropHelper.rowDragEnd.on(() => {
            this._selectedValues = filterNotDefined(
                table.dragAndDropHelper.getTableItemsOrdered().map(item => this._getItemValue(item))
            );

            // dispatch onchange event
            this._dispatchValueChangedEvent(this._selectedValues);
        });
    }

    /**
     * Callback: When an item is added to selectedItemsList
     */
    public onAdd(value: ValueType<Value>): void {
        this.getSelectedValues().push(value);
        this.addEvent.emit({
            value,
            item: this._getItemByValue(this.data, value)
        });
    }

    /**
     * Callback: When an item is removed to selectedItemsList
     */
    public onRemove(value: ValueType<Value>): void {
        this._selectedValues = this.getSelectedValues().filter(val => val !== value);

        // dispatch onchange event
        this._dispatchValueChangedEvent(this._selectedValues);

        this.removeEvent.emit({
            value,
            item: this._getItemByValue(this.data, value)
        });
    }

    /** Define callbacks for SmartSelect if they're not defined in options */
    private _refreshDefaultCallbacks(): void {
        if (!this.smartSelectOptions.itemFormatter) {
            this.smartSelectOptions.setWithoutRefresh({
                itemFormatter: this._defaultItemFormatter
            });
        }
    }

    /** Get item value from smartSelect */
    private _getItemValue(item: ItemType): ValueType<Value> | undefined {
        if (this.smartSelectOptions.callbacksToOverride?.getItemValue) {
            return this.smartSelectOptions.callbacksToOverride.getItemValue(item);
        }
        return (item as anyReallyAny).value as ValueType<Value>; // Cast necessary to compile, method will be override in most cases
    }

    /** Get item by value from smartSelect */
    private _getItemByValue(items: ItemType[], value: ValueType<Value>): ItemType | undefined {
        if (this.smartSelectOptions.callbacksToOverride?.getItemByValue) {
            return this.smartSelectOptions.callbacksToOverride.getItemByValue(items, value);
        }
        if (value != null || !items) { return undefined; }

        return items.find((item: ItemType) => this._getItemValue(item) === value);
    }

    /**
     * Callback: Format a given item, to an HTMLElement or a string to display in <select> and selectedValuesList*
     * If a string is returned, the string is append in an <span> element
     */
    private _defaultItemFormatter: (item: ItemType) => AcceptedHtmlElementContent = (item: ItemType) => {
        return createHtmlElement("div", {
            innerHTML: createHtmlElement("span", { innerHTML: typeof item === "string" || item instanceof HTMLElement ? item : "", cssClass: [FONT_WEIGHT.BOLD] }),
            cssClass: ["smart-select-item-element", FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, ALIGN_ITEMS.CENTER, PADDING.PS_1]
        });
    };

    /**
     * Get items to display in select div
     * Filter full list, remove the item if his value is already in selectedValuesList
     */
    protected _getSelectItemsList(): ItemType[] {
        return this.data.filter((item: ItemType) => {
            const value = this._getItemValue(item);
            return value != null && !this.getSelectedValues().includes(value);
        });
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): Value[] | undefined {
        return this._selectedValues;
    }

    /**
     * Get HTMLElement for an item from formatItem() method
     */
    protected _getItemHtmlElement(item: ItemType): HTMLElement {
        if (!this.smartSelectOptions.itemFormatter) {
            getLogService().error("[ReferenceListEditor] itemFormatter not defined in smartSelectOptions");
            return createHtmlElement("div");
        }

        let itemFormatted = this.smartSelectOptions.itemFormatter(item);


        if (this.options.selectedValueDivFormatter) {
            itemFormatted = this.options.selectedValueDivFormatter(item, itemFormatted);
        }

        // If format item return a string, add it in <span> element
        if (typeof itemFormatted === "string") {
            itemFormatted = createHtmlElement("span", {
                innerHTML: itemFormatted
            });
        }
        itemFormatted.classList.add(SELECTED_VALUE_DIV);

        return itemFormatted;
    }

    /**
     * Create the selected value div element
     * @param item Item used to create divElement
     */
    protected _createSelectedValueDiv(item: ItemType): HTMLElement {

        // Create div selected value container
        const div = createHtmlElement("div", {
            cssClass: [FLEX_CONTAINER_ROW, SELECTED_VALUE_CONTAINER, ALIGN_ITEMS.CENTER, W_100, H_100],
            innerHTML: [
                this._getItemHtmlElement(item),
                this._buildRemoveButton(item)
            ]
        });

        return div;
    }

    /** Build remove button for a given item */
    protected _buildRemoveButton(item: ItemType): AcceptedHtmlElementContent {
        // Don't append remove button if disabled
        if (this.disabled || !item) { return ""; }
        // Copy options for removeButton
        let btnOptions: ButtonOptions = {
            ...this.removeButtonOptions
        };

        // Add tooltip to remove button if not given
        const itemValue = this._getItemValue(item);

        if (!this.removeButtonOptions.tooltip && this.options.enableRemoveButtonTooltip) {
            btnOptions = {
                ...btnOptions,
                tooltip: {
                    delay: 200,
                    title: itemValue != null ? this.i18n.removeItemTooltip({ value: String(itemValue) }) : `Remove item ${itemValue}`// TODO i18n
                }
            };
        }

        // Create the remove item button
        const removeButton = new CjsButton(btnOptions);
        // Add click listener to remove button
        removeButton.clickEvent.on(() => {
            if (!this.disabled) {
                const value = this._getItemValue(item);
                if (value != null) {
                    this.onRemove(value);
                    this.refresh();
                }
            }
        });

        // Add remove button to div selected value container
        return removeButton;
    }

    /**
     * When <select> value change
     */
    protected override _onInputValueChange(selectedValue: ValueType<Value>): void {

        if (!this.disabled) {

            // Call onAdd callback
            this.onAdd(selectedValue);

            // Refresh the component
            this.refresh();

            // dispatch onchange event
            this._dispatchValueChangedEvent(this._selectedValues);
        }

    }

    /** Return selected items */
    protected _getSelectedItems(): ItemType[] {
        const items: ItemType[] = [];
        for (const value of this.getSelectedValues()) {
            const item = this._getItemByValue(this.data, value as ValueType<Value>);
            if (item != null) { items.push(item); }
        }
        return items;
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        // Set attribute disabled
        toggleAttribute(this.components.selectedValuesContainer, this.disabled, "disabled", "");

        // Refresh disabled on smart select
        this.smartSelectOptions.setWithoutRefresh({ disabled: this.disabled });
        this._refreshSmartSelect();

        // Hide smart select if there are selected values
        toggleVisible(this.components.smartSelect, !(this.disabled && this._selectedValues.length), Display.BLOCK);
    }

}
